// src/components/sandbox/LLMSandboxTab.js
import { useState, useRef } from "react";
import ChatSession from "../ChatSession";
import { AGENT_GUIDE, useLLMClient } from "../../llm/LLMClient";
import "./LLMSandboxTab.css";

const STORAGE_KEY_STRATEGY = "llm-sandbox-memory-strategy";
const STORAGE_KEY_ITEMS = "llm-sandbox-memory-items";
const STORAGE_KEY_TEMPLATES = "llm-sandbox-edited-templates";

const STRATEGIES = [
  { value: "none", label: "None", disabled: false },
  { value: "local", label: "Local text", disabled: false },
  { value: "hindsight", label: "Hindsight (coming soon)", disabled: true },
  { value: "claude-api", label: "Claude API (coming soon)", disabled: true },
];

const loadStrategy = () => localStorage.getItem(STORAGE_KEY_STRATEGY) || "local";
const loadItems = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY_ITEMS)) || [];
  } catch {
    return [];
  }
};
const loadEditedTemplates = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY_TEMPLATES)) || {};
  } catch {
    return {};
  }
};

const LLMSandboxTab = ({ userStore, appStatusStore }) => {
  const [strategy, setStrategy] = useState(loadStrategy);
  const [items, setItems] = useState(loadItems);
  const [addText, setAddText] = useState("");
  const addInputRef = useRef(null);

  const { aiTemplates = {} } = appStatusStore || {};
  const [editedTemplates, setEditedTemplates] = useState(loadEditedTemplates);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateText, setTemplateText] = useState("");

  const client = useLLMClient(AGENT_GUIDE, null, userStore?.authData, userStore, appStatusStore);

  const saveStrategy = (s) => {
    setStrategy(s);
    localStorage.setItem(STORAGE_KEY_STRATEGY, s);
  };

  const saveItems = (newItems) => {
    setItems(newItems);
    localStorage.setItem(STORAGE_KEY_ITEMS, JSON.stringify(newItems));
  };

  const handleAddItem = () => {
    const text = addText.trim();
    if (!text) return;
    saveItems([...items, text]);
    setAddText("");
    addInputRef.current?.focus();
  };

  const handleRemoveItem = (idx) => {
    saveItems(items.filter((_, i) => i !== idx));
  };

  const handleAddKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddItem();
    }
  };

  const saveEditedTemplates = (next) => {
    setEditedTemplates(next);
    localStorage.setItem(STORAGE_KEY_TEMPLATES, JSON.stringify(next));
  };

  const handleTemplateSelect = (key) => {
    setSelectedTemplate(key);
    setTemplateText(editedTemplates[key] ?? aiTemplates[key] ?? "");
  };

  const handleTemplateTextChange = (e) => {
    const text = e.target.value;
    setTemplateText(text);
    const next = { ...editedTemplates };
    if (text) {
      next[selectedTemplate] = text;
    } else {
      delete next[selectedTemplate];
    }
    saveEditedTemplates(next);
  };

  // memoryKey encodes strategy and items only — template edits take effect on next "New conversation"
  // (ChatSession remounts), not mid-session while the user is still typing in the editor
  const memoryKey = `${strategy}:${JSON.stringify(items)}`;

  // Merge memory items (as extra-prompt) with edited templates; memory takes precedence for extra-prompt
  const memoryExtraPrompt = strategy === "local" && items.length > 0 ? items.join("\n") : null;
  const initialTemplates = {
    ...editedTemplates,
    ...(memoryExtraPrompt ? { "extra-prompt": memoryExtraPrompt } : {}),
  };

  return (
    <div className="llm-sandbox-tab">
      <div className="llm-memory-controls">
        <div className="memory-strategy-row">
          <label htmlFor="memory-strategy">Memory:</label>
          <select
            id="memory-strategy"
            value={strategy}
            onChange={(e) => saveStrategy(e.target.value)}
          >
            {STRATEGIES.map(({ value, label, disabled }) => (
              <option key={value} value={value} disabled={disabled}>
                {label}
              </option>
            ))}
          </select>
        </div>

        {strategy === "local" && (
          <>
            <ul className="memory-items-list">
              {items.map((item, idx) => (
                <li key={idx}>
                  <span>• {item}</span>
                  <button
                    title="Remove this memory item"
                    onClick={() => handleRemoveItem(idx)}
                  >
                    ✕
                  </button>
                </li>
              ))}
            </ul>
            <div className="memory-add-row">
              <input
                ref={addInputRef}
                type="text"
                placeholder="Add a memory item..."
                value={addText}
                onChange={(e) => setAddText(e.target.value)}
                onKeyDown={handleAddKeyDown}
              />
              <button
                className="btn btn-sm btn-default"
                onClick={handleAddItem}
                disabled={!addText.trim()}
              >
                + Add
              </button>
            </div>
          </>
        )}
      </div>

      <div className="llm-template-controls">
        <div className="template-controls-row">
          <label>Templates:</label>
          <select
            value={selectedTemplate || ""}
            onChange={(e) => handleTemplateSelect(e.target.value)}
          >
            <option value="" disabled>Select a template to edit...</option>
            {Object.keys(aiTemplates).map((key) => (
              <option key={key} value={key}>
                {key}{editedTemplates[key] !== undefined ? " *" : ""}
              </option>
            ))}
          </select>
          {selectedTemplate && editedTemplates[selectedTemplate] !== undefined && (
            <button
              className="btn btn-sm btn-default"
              title="Reset to server default"
              onClick={() => {
                const next = { ...editedTemplates };
                delete next[selectedTemplate];
                saveEditedTemplates(next);
                setTemplateText(aiTemplates[selectedTemplate] ?? "");
              }}
            >
              Reset
            </button>
          )}
        </div>
        {selectedTemplate && (
          <textarea
            className="power-user template-editor"
            value={templateText}
            onChange={handleTemplateTextChange}
            onKeyDown={(e) => { if (e.key === "Escape") { setTemplateText(aiTemplates[selectedTemplate] ?? ""); } }}
            placeholder={`Edit template: ${selectedTemplate}`}
          />
        )}
      </div>

      <ChatSession
        key={memoryKey}
        client={client}
        userStore={userStore}
        appStatusStore={appStatusStore}
        sessionAgent={AGENT_GUIDE}
        useTools={true}
        allowAttachments={true}
        showSettings={false}
        showWelcome={false}
        autoFocus={true}
        initialTemplates={initialTemplates}
      />
    </div>
  );
};

export default LLMSandboxTab;